import { useGetBugCountsQuery } from '../hooks/bug-management/useProjectFindings'
import SiteMapTab from '../components/SiteMapTab'
import { useExperimentalFlag } from '../hooks/useExperimentalFlag'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useNavigate, Navigate } from '../components/nav'
import { useSelector, useDispatch } from 'react-redux'
import { LogIn, RefreshCw } from 'lucide-react'
import type { RootState, AppDispatch } from '../store'
import ProjectHeader from '../components/ProjectHeader'
import TabNavigation from '../components/TabNavigation'
import { usePlaywrightTestsEnabled } from '../hooks/usePlaywrightTestsEnabled'
import { projectTabs } from '../components/projectTabs'
import OverviewTab from '../components/OverviewTab'
import BugListTab from '../components/bug-management/BugListTab'
import TestRunListTab from '../components/TestRunListTab'
import PlaywrightTestsTab from '../components/PlaywrightTestsTab'
import SecurityTab from '../components/SecurityTab'
import ExplorationsTab from '../components/ExplorationsTab'
import EmbeddedLogin from '../components/EmbeddedLogin'
import { login, postLoginReturnTo } from '../components/UserMenu'
import { useSignInOnUnauthorized } from '../hooks/useSignInOnUnauthorized'
import { isProjectDetailTab } from '../projectDetailRoutes'
import { useEmbedded } from '../lib/embed'
import { useGetProjectMembersQuery, useGetProjectQuery } from '../services/projectDataApi'
import { projectQueryErrorMessage } from '../lib/projectQueryError'
import { projectPause } from '../lib/projectPause'
import './ProjectDetail.css'

export default function ProjectDetail() {
  const { projectId, tab } = useParams<{ projectId: string; tab: string }>()
  const { enabled: continuousTesting } = useExperimentalFlag('continuous_testing')
  const { enabled: siteMapEnabled } = useExperimentalFlag('site_map')
  const embedded = useEmbedded()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const user = useSelector((state: RootState) => state.auth.user)
  const projectQuery = useGetProjectQuery(projectId!, { skip: !projectId })
  const membersQuery = useGetProjectMembersQuery(projectId!, { skip: !projectId })
  const bugCountsQuery = useGetBugCountsQuery(projectId!, { skip: !projectId })

  const { enabled: playwrightEnabled } = usePlaywrightTestsEnabled(projectId ?? '')
  const project = projectQuery.data ?? null
  const loading = projectQuery.isLoading
  const error = projectQueryErrorMessage(projectQuery.error, Boolean(user))
  // A private project opened while signed out goes to sign-in and comes back here, rather than
  // dead-ending on the error state below.
  const redirectingToLogin = useSignInOnUnauthorized(projectQuery.error)
  const userRole = membersQuery.data?.role
  const canEdit = userRole === 'owner' || userRole === 'admin'
  // The Bugs tab badge counts open reports.
  const openBugCount = (bugCountsQuery.data?.openCount ?? 0)

  useEffect(() => {
    if (project) {
      document.title = `Replay QA: ${project.name}`
    }
    return () => { document.title = 'Replay QA' }
  }, [project])

  const resolvedTab = tab
  if (!projectId || !resolvedTab || !isProjectDetailTab(resolvedTab)) {
    return projectId ? (
      <Navigate to={`/projects/${projectId}/overview`} replace />
    ) : (
      <Navigate to="/projects" replace />
    )
  }

  const activeTab = resolvedTab

  // Render nothing for the moment it takes the sign-in redirect to leave the page.
  if (redirectingToLogin) return null

  if (!loading && error) {
    // In the cross-site embed, a load failure for a private project is almost always missing auth.
    // The iframe can't redirect to the login provider itself, so offer an in-frame popup login.
    if (embedded) {
      if (!user) {
        return (
          <div className="embedded-error" data-testid="project-error">
            <EmbeddedLogin message={error ?? undefined} />
          </div>
        )
      }
      return (
        <div className="embedded-error" role="alert" data-testid="project-error">
          <p>{error ?? 'Project not found'}</p>
        </div>
      )
    }
    return (
      <div className="project-detail__error" role="alert" data-testid="project-error">
        <p>{error ?? 'Project not found'}</p>
        <div className="project-detail__error-actions">
          {!user && error?.includes('Login required') && (
            <button type="button" onClick={() => login(dispatch, postLoginReturnTo(window.location.pathname, window.location.search))} data-testid="login-btn" data-analytics-event="project-error-signin-button">
              <LogIn size={14} aria-hidden="true" />
              Sign in
            </button>
          )}
          {error && projectId && (
            <button type="button" onClick={() => void projectQuery.refetch()} data-testid="retry-btn" data-analytics-event="project-error-retry">
              <RefreshCw size={14} aria-hidden="true" />
              Retry
            </button>
          )}
          <button type="button" onClick={() => navigate('/')} className="project-detail__error-back-btn" data-analytics-event="project-error-back-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  // Links are written against the canonical `/projects` paths; the embed-aware Link/NavLink rewrite
  // them to `/p/...` when this page renders inside the iframe, so one base path works in both trees.
  const tabBasePath = `/projects/${projectId}`

  // Overview + Bugs + Test Runs are the project execution surfaces. GitHub configuration is managed
  // from Settings, while PR results are joined into Test Runs by the frontend.
  const tabs = projectTabs({
    continuousTesting,
    siteMapEnabled,
    playwrightEnabled,
    openBugCount,
    securityEnabled: project?.enabled_polish_passes.includes('security') === true,
  })

  // The tab sheet is identical in both layouts; only the surrounding chrome differs (the full app
  // adds an editable project masthead, the embed omits it since EmbeddedShell supplies the frame).
  const sheet = (
    <div className="project-detail__sheet">
      <div className="project-detail__sheet-scroll">
        <TabNavigation tabs={tabs} activeTab={activeTab} basePath={tabBasePath} />
        <div
          className="project-detail__panel"
          role="tabpanel"
          id={`project-tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === 'overview' && <OverviewTab projectId={projectId} />}
          {activeTab === 'explorations' && <ExplorationsTab projectId={projectId} />}
          {activeTab === 'bugs' && <BugListTab projectId={projectId} project={project} />}
          {activeTab === 'test-runs' && <TestRunListTab projectId={projectId} pause={projectPause(project)} />}
          {activeTab === 'site-map' && <SiteMapTab key={projectId} projectId={projectId!} />}
          {activeTab === 'playwright-tests' && <PlaywrightTestsTab key={projectId} projectId={projectId} />}
          {activeTab === 'security' && <SecurityTab projectId={projectId} project={project} />}
        </div>
      </div>
    </div>
  )

  if (embedded) {
    return (
      <div className="project-detail" data-testid="embedded-project-detail-page">
        {sheet}
      </div>
    )
  }

  return (
    <div className="project-detail" data-testid="project-detail-page">
      <div className="project-detail__masthead">
        {loading || !project ? (
          <div role="status" aria-live="polite" data-testid="project-loading">
            <div className="project-detail__skeleton-header">
              <div className="project-detail__skeleton-title" />
              <div className="project-detail__skeleton-url" />
            </div>
          </div>
        ) : (
          <ProjectHeader project={project} readOnly={!canEdit} shared={userRole === 'member'} />
        )}
      </div>

      {sheet}
    </div>
  )
}
